// src/pages/conocimiento/components/ArticulosList.tsx
import { Link } from 'react-router-dom';
import { Eye, FileText } from 'lucide-react';
import { Badge } from '@/components/ui';
import {
  TIPO_ARTICULO_LABELS,
  TIPO_ARTICULO_COLORS,
  ESTADO_ARTICULO_LABELS,
  ESTADO_ARTICULO_COLORS,
} from '@/types';
import type { ArticuloListItem } from '@/types';

interface ArticulosListProps {
  articulos: ArticuloListItem[];
  isLoading?: boolean;
}

function formatNumber(numero: number): string {
  return `KB-${numero.toString().padStart(4, '0')}`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function ArticulosList({ articulos, isLoading }: ArticulosListProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="bg-white border rounded-lg p-4 animate-pulse"
          >
            <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (!articulos || articulos.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-8 text-center">
        <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          No hay artículos
        </h3>
        <p className="text-gray-500">
          Crea tu primer artículo de conocimiento
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {articulos.map((articulo) => (
        <Link
          key={articulo.id}
          to={`/conocimiento/${articulo.id}`}
          className="block bg-white border rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-gray-500 font-mono">
                  {formatNumber(articulo.numero)}
                </span>
                <Badge className={TIPO_ARTICULO_COLORS[articulo.tipo]}>
                  {TIPO_ARTICULO_LABELS[articulo.tipo]}
                </Badge>
                <Badge className={ESTADO_ARTICULO_COLORS[articulo.estado]}>
                  {ESTADO_ARTICULO_LABELS[articulo.estado]}
                </Badge>
              </div>
              <h3 className="text-base font-medium text-gray-900 truncate">
                {articulo.titulo}
              </h3>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                {articulo.categoria_nombre && (
                  <span>{articulo.categoria_nombre}</span>
                )}
                <span>{formatDate(articulo.created_at)}</span>
                <span className="flex items-center gap-1">
                  <Eye className="h-3.5 w-3.5" />
                  {articulo.vistas_totales}
                </span>
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
